import styled from 'styled-components';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useQuery } from '@tanstack/react-query';
import {
  Space,
  Skeleton,
  Popconfirm,
  message,
  Popover,
  Table,
  Spin,
  Input,
} from 'antd';
import * as React from 'react';
import { useState, useEffect, useRef, useCallback } from 'react';
import Flag from 'react-world-flags';
import { PlusOutlined } from '@ant-design/icons';
import { useDebounce } from 'use-debounce';

import useTranslation from 'customHooks/translate';
import { queryClient } from 'config/query-client';
import { DEBITORS } from 'events/types';
import AddNewPartnerForm from 'pages/Dialogs/AddNewPartnerForm';
import { deleteDebitor, getMyDebitors } from 'services/partners';
import { toastError } from 'services/helpers/getError';
import { DebitorsDataType } from 'types';

import '../../components/profile/styles/Partners.css';
import '../../pages/styles/partnersTable.css';

const Debitors = () => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const translation = useTranslation();

  const handleClose = () => {
    setAnchorEl(null);
  };

  const [pageSize] = useState(15);
  const [pageIndex, setPageIndex] = useState(0);
  const [tableData, setTableData] = useState<DebitorsDataType[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef<HTMLDivElement>(null);
  const isFetchingRef = useRef(false);
  const isComponentMounted = useRef(false);

  const [addCustomerFormOpen, setAddCustomerFormOpen] = useState(false);
  const [addCustomerFormType, setaddCustomerFormType] = useState('debitor');
  const [partnerToEdit, setPartnerToEdit] = useState<DebitorsDataType>();

  const [partnerName, setPartnerName] = useState('');
  const [debouncedPartnerName] = useDebounce(partnerName, 800);

  const openAddCustomerForm = (type: string) => {
    setaddCustomerFormType(type);
    setAddCustomerFormOpen(true);
    setPartnerToEdit(undefined);
  };

  const handleCloseAddCustomerType = () => {
    setAddCustomerFormOpen(false);
  };

  const handleDelete = async (record) => {
    try {
      await deleteDebitor(record.id);
      message.success(`${translation.messages.successDeleted}`);
      resetData();
      refetch();
      queryClient.invalidateQueries({
        queryKey: [DEBITORS],
        refetchType: 'all',
      });
    } catch (error) {
      toastError(error);
    }
  };

  const resetData = () => {
    setTableData([]);
    setPageIndex(0);
    setHasMore(true);
  };

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const fetchData = useCallback(
    async (
      currentPage: number,
      isNewRequest: boolean = false,
      partnerNameFilter?: string
    ) => {
      if (isFetchingRef.current) return null;

      isFetchingRef.current = true;
      setIsLoadingMore(true);

      try {
        const response = await getMyDebitors(
          pageSize,
          currentPage,
          partnerNameFilter
        );

        const data = response?.data?.data ?? [];
        const totalRecord = response?.data?.totalRecord ?? 0;

        if (data.length) {
          const newData = data.map((item: any) => ({
            ...item,
            key: item.id,
          }));

          setTotalCount(totalRecord);

          if (isNewRequest) {
            setTableData(newData);
          } else {
            setTableData((prev) => [...prev, ...newData]);
          }

          const newTotalItems =
            (isNewRequest ? 0 : tableData.length) + newData.length;
          const hasMoreItems = newTotalItems < totalRecord;
          setHasMore(hasMoreItems);

          return response.data;
        }

        if (isNewRequest) {
          setTableData([]);
          setTotalCount(totalRecord);
          setHasMore(false);
        }
        return null;
      } catch (error: any) {
        message.error(
          error?.response?.data.Message
            ? error?.response?.data.Message
            : 'Permission required!'
        );
        return null;
      } finally {
        setIsLoadingMore(false);
        isFetchingRef.current = false;
      }
    },
    [pageSize, tableData.length]
  );



  const columns = [
    {
      key: 'index',
      title: <span>No</span>,
      align: 'center',
      width: 80,
      render: (_, __, index) => (
        <div style={{ textAlign: 'center' }}>{index + 1}</div>
      ),
    },
    {
      key: 'name',
      title: (
        <span>{translation.partners.PartnerName}</span>
      ),
      dataIndex: 'name',
      width: 300,
      align: 'center',
    },
    {
      key: 'country',
      title: (
        <span>
          {translation.partners.PartnerCountry}
        </span>
      ),
      dataIndex: 'country',
      align: 'center',
      width: 300,
      render: (text, record) => (
        <div
          style={{
            display: 'flex',
            gap: '10px',
            justifyContent: 'center',
            background: 'rgba(70, 106, 251, 0.03);',
          }}
        >
          <Flag
            className="h-6 w-6 overflow-hidden rounded-md"
            code={record.countryCode}
          />
          {text}
        </div>
      ),
    },
    {
      title: <span>{translation.action.Action}</span>,
      dataIndex: 'actions',
      align: 'center',
      key: 'actions',
      width: 200,
      render: (_, record) =>
        isMobile ? (
          <Popover
            placement="bottom"
            trigger="click"
            content={
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '20px',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <EditDeleteIcon
                  src="assets/edit-icon.svg"
                  alt="Edit"
                  onClick={() => {
                    setPartnerToEdit(record);
                    setAddCustomerFormOpen(true);
                  }}
                />
                <Popconfirm
                  title={translation.DeleteItem}
                  onConfirm={() => handleDelete(record)}
                  okText="Yes"
                  placement="left"
                  cancelText="No"
                >
                  <EditDeleteIcon src="assets/delete-icon.svg" alt="Delete" />
                </Popconfirm>
              </div>
            }
          >
            <MoreVertIcon
              style={{
                fontSize: '29px',
                border: '1px solid #292d32bf',
                borderRadius: '50%',
                padding: '4px',
              }}
            />
          </Popover>
        ) : (
          <Space size="middle">
            <div style={{ cursor: 'pointer' }}>
              <img
                className="edit"
                src="assets/edit-icon.svg"
                alt="Edit"
                onClick={() => {
                  setPartnerToEdit(record);
                  setAddCustomerFormOpen(true);
                }}
              />
            </div>
            <Popconfirm
              title={translation.DeleteItem}
              onConfirm={() => handleDelete(record)}
              okText="Yes"
              cancelText="No"
            >
              <div style={{ cursor: 'pointer' }}>
                <img
                  className="delete"
                  src="assets/delete-icon.svg"
                  alt="Delete"
                />
              </div>
            </Popconfirm>
          </Space>
        ),
    },
  ];

  // Initial data fetch
  const { isLoading, refetch } = useQuery(
    [DEBITORS, debouncedPartnerName],
    () => fetchData(0, true, debouncedPartnerName),
    {
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      enabled: true,
      staleTime: 0,
      cacheTime: 60000,
    }
  );

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && isComponentMounted.current) {
        resetData();
        refetch();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refetch]);

  useEffect(() => {
    isComponentMounted.current = true;
    resetData();
    refetch();
    return () => {
      isComponentMounted.current = false;
    };
  }, [refetch]);

  useEffect(() => {
    const handleRouteChange = () => {
      if (isComponentMounted.current) {
        if (tableData.length === 0) {
          refetch();
        }
      }
    };

    window.addEventListener('popstate', handleRouteChange);

    return () => {
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, [refetch, tableData.length]);

  // Handle infinite scrolling
  const handleScroll = useCallback(() => {
    if (
      !tableContainerRef.current ||
      !loadingRef.current ||
      isFetchingRef.current ||
      !hasMore ||
      isLoading
    )
      return;

    const containerRect = tableContainerRef.current.getBoundingClientRect();
    const loadingRect = loadingRef.current.getBoundingClientRect();

    // Check if the loading element is visible
    if (loadingRect.top <= containerRect.bottom + 100) {
      const nextPage = pageIndex + 1;
      setPageIndex(nextPage);
      fetchData(nextPage, false, debouncedPartnerName);
    }
  }, [pageIndex, hasMore, isLoading, fetchData, debouncedPartnerName]);

  // Set up scroll event listener
  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => {
        container.removeEventListener('scroll', handleScroll);
      };
    }
  }, [handleScroll]);

  const handleAddDebitor = () => {
    openAddCustomerForm('debitor');
    handleClose();
  };

  return (
    <div>
      <div className="mb-[20px] flex w-full flex-wrap items-center justify-between gap-[10px]">
        <p className="text-[20px] font-medium text-[#1D1D1D] md:text-[24px]">
          {translation.profile.debitors}
        </p>
        <Input
          onChange={(event) => setPartnerName(event?.target?.value)}
          value={partnerName}
          className="max-h-10 w-full rounded-[8px] border-none bg-[#F6F6F6] text-[#1D1D1D] transition-all duration-300 ease-in-out hover:bg-gray-100 sm:w-[280px]"
          placeholder={translation.dashboard.PartnerSearchDr}
        />
        <button
          onClick={handleAddDebitor}
          className="flex w-auto w-full w-max items-center gap-[8px] rounded-[6px] rounded-[6px] bg-[#466afb] px-[16px] py-[8px] text-[16px] font-normal leading-[16px] text-white md:px-[24px] md:py-[12px]"
        >
          <PlusOutlined />

          <p className="hidden md:block">{translation.partners.AddNewButton}</p>
        </button>
      </div>
      {isLoading && tableData.length === 0 ? (
        <Skeleton active />
      ) : (
        <div
          className="relative h-[500px] overflow-y-auto"
          ref={tableContainerRef}
        >
          <Table
            sticky={{
              offsetHeader: 0,
            }}
            bordered
            className="custom-bordered"
            dataSource={tableData}
            columns={columns as any[]}
            loading={isLoading && pageIndex === 0}
            pagination={false}
            rowClassName={(_, i) =>
              i % 2 === 0 ? 'bg-[#466AFB08]' : 'bg-white'
            }
          />
          <LoadingIndicator ref={loadingRef}>
            {isLoadingMore && <Spin size="small" />}
            {!hasMore && tableData.length > 0 && (
              <div style={{ color: '#999', marginTop: '8px' }}>
                {translation.messages.allData}
              </div>
            )}
          </LoadingIndicator>
        </div>
      )}

      <AddNewPartnerForm
        open={addCustomerFormOpen}
        transactionType={addCustomerFormType}
        handleClose={handleCloseAddCustomerType}
        partnerToEdit={partnerToEdit}
        onPartnerChange={() => {
          resetData();
          refetch();
        }}
        isCreditor={false}
      />
    </div>
  );
};

export default Debitors;

const EditDeleteIcon = styled.img`
  cursor: pointer;
  width: 24px;
  height: 24px;
`;

const LoadingIndicator = styled.div`
  text-align: center;
  padding: 16px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50px;
`;
